import type { TrendRange } from './types';

export const CLINIC_USAGE_METRICS_ENDPOINT = '/api/clinic/usage/metrics';

const RANGE_DAYS: Record<TrendRange, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

/**
 * Map a preset range to a half-open [from, to) window. `to` is now; `from` is
 * the start of the UTC day `days - 1` days ago, so a 7d window spans 7 calendar
 * days including today. The 7/30/90 buttons are just shortcuts for these dates —
 * the endpoint itself is generic over from/to.
 */
export const rangeToWindow = (range: TrendRange, now: Date = new Date()): { from: string; to: string } => {
  const days = RANGE_DAYS[range];
  const startDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  startDay.setUTCDate(startDay.getUTCDate() - (days - 1));
  return { from: startDay.toISOString(), to: now.toISOString() };
};

export class ClinicUsageApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ClinicUsageApiError';
    this.status = status;
  }
}

export const fetchClinicUsageMetrics = async <T>(
  window: { from: string; to: string },
  options: { signal?: AbortSignal } = {}
): Promise<T> => {
  const query = new URLSearchParams(window).toString();
  const response = await fetch(`${CLINIC_USAGE_METRICS_ENDPOINT}?${query}`, {
    credentials: 'include',
    signal: options.signal,
  });
  if (!response.ok) {
    throw new ClinicUsageApiError(`Request failed: ${response.status}`, response.status);
  }
  const payload = await response.json();
  return payload.data as T;
};
